import isArray from 'lodash/isArray'
import isPlainObject from 'lodash/isPlainObject'
import { formatPreferredLocations } from './candidateHelpers'

const snakeToCamelCase = snakeCasedKey => {
  const cameledKey = snakeCasedKey.replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
  return cameledKey
}

export const formatRestToGraphQL = snakedObject => {
  if (isPlainObject(snakedObject)) {
    const cameledObject = {}
    Object.keys(snakedObject).forEach(snakeKey => {
      cameledObject[snakeToCamelCase(snakeKey)] = formatRestToGraphQL(snakedObject[snakeKey])
      if (snakeKey === 'country_iso2_code' || snakeKey === 'type' || snakeKey === 'location_category') {
        cameledObject[snakeToCamelCase(snakeKey)] = cameledObject[snakeToCamelCase(snakeKey)].toUpperCase()
      }
    })

    return cameledObject
  }
  if (isArray(snakedObject)) {
    const cameledArray = snakedObject.map(arrayEntry => formatRestToGraphQL(arrayEntry))

    return cameledArray
  }
  return snakedObject
}

export const emptyWorkplacePolicyDefaultValues = {
  cities: {
    hybrid: [],
    office: [],
  },
  policies: {
    hybrid: false,
    office: false,
    remote: false,
  },
}

/**
 * Checks that comprehensive locations exist on an entry and adds them if they don't
 * @param {Array.<Object>} comprehensiveLocations
 * @param {Array.<string>} selectedCities
 * @param {Array.<Object>} locationOsmStrings
 * @returns {Array.<Object>} [] non-empty array of comprehensive locations
 */
const checkComprehensiveLocations = (comprehensiveLocations, selectedCities, locationOsmStrings) => {
  if (comprehensiveLocations.length === 0 && selectedCities.length > 0) {
    const { selectedPreferredLocations } = formatPreferredLocations({
      cities: selectedCities,
      locationOsmStrings,
    })

    return selectedPreferredLocations
  }
  return comprehensiveLocations
}

const formatCities = (remotePolicies, type) => {
  const formattedCities = remotePolicies
    .filter(policy => policy.type.toLowerCase() === type)
    .flatMap(policy => policy.city_ids || policy.cities)
    .reduce((cities, city) => [...cities, typeof city === 'string' ? city : city.id], [])

  return formattedCities
}

/**
 * Formats workplace policy default form values
 * @param {Array.<Object>} remotePolicies
 * @returns {Object} workplacePoliciesDefaultValues | emptyWorkplacePolicyDefaultValues
 */
export const formatWorkplacePolicyForDefaultValues = remotePolicies => {
  if (remotePolicies) {
    const defaultValues = {
      cities: {
        hybrid: formatCities(remotePolicies, 'hybrid'),
        office: formatCities(remotePolicies, 'office'),
      },
      policies: {
        hybrid: remotePolicies.some(policy => policy.type.toLowerCase() === 'hybrid'),
        remote: remotePolicies.some(policy => policy.type.toLowerCase() === 'remote'),
        office: remotePolicies.some(policy => policy.type.toLowerCase() === 'office'),
      },
    }

    return defaultValues
  }

  return emptyWorkplacePolicyDefaultValues
}

/**
 * Formats workplace policies for submit
 * @param {Object} formData
 * @param {Array.<Object>} locationOsmStrings
 * @returns {Array.<Object>}
 */
export const formatWorkplacePolicyForSubmit = (formData, locationOsmStrings) => {
  const remotePolicies = []
  if (formData.policies.remote) {
    remotePolicies.push({
      type: 'remote',
      city_ids: [],
      comprehensive_locations: [],
    })
  }

  const officeComprehensiveLocations = formData.comprehensiveLocations.office
  const officeCities = formData.cities?.office
  if (formData.policies.office) {
    remotePolicies.push({
      type: 'office',
      city_ids: officeCities,
      comprehensive_locations: checkComprehensiveLocations(officeComprehensiveLocations, officeCities, locationOsmStrings),
    })
  }

  const hybridComprehensiveLocations = formData.comprehensiveLocations.hybrid
  const hybridCities = formData.cities?.hybrid
  if (formData.policies.hybrid) {
    remotePolicies.push({
      type: 'hybrid',
      city_ids: hybridCities,
      comprehensive_locations: checkComprehensiveLocations(hybridComprehensiveLocations, hybridCities, locationOsmStrings),
    })
  }

  return remotePolicies
}
